// @flow
import * as React from 'react';
import { ListPageWrapper, TabLabel, useSessionStorageState } from '../../shared';
import { Stack, Tab as MuiTab, TabProps, Tabs } from '@mui/material';
import { Link, LinkProps, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { getRoute } from '../../app/router';
import { getIsAgentManager, keycloak, KeycloakRoles } from '@coverright/shared/keycloak';
import { AgentSelector } from '../../features';
import { useEffect } from 'react';
import useAgentDashboardCounters from './hooks/use-agent-dashboard-counters';
import { OutdatedLeadsModal } from './ui/outdated-leads-modal';

export const Dashboard = () => {
  const contextAgentId = keycloak?.tokenParsed?.sub;
  const {agentId} = useParams<{ agentId?: string }>();
  const [agent, setAgent] = useAgent(agentId || contextAgentId)
  const {counters} = useAgentDashboardCounters(agent);
  const location = useLocation();
  const navigate = useNavigate();

  const isAlfa = keycloak?.realmAccess?.roles?.includes(KeycloakRoles.ROLE_ALFA)

  const isManager = getIsAgentManager(keycloak);

  const tab = location.pathname.split('/').at(2);
  const mainTab = tab || isAlfa ? 'inbox' : 'today-sql';

  const getLink = (route: string) => `/${getRoute('dashboard')}/${route}/${agent}`;

  useEffect(() => {
    const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const id = uuidRegex.exec(location.pathname)?.shift()

    if (id) {
      if (id !== agent) {
        setAgent(id)
      }
    } else if (agent) {
      navigate(getLink('today-sql'), { replace: true });
    }
  }, [agent, location.pathname])

  const changeAgent = (id: string) => {
    const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const existing = uuidRegex.exec(location.pathname)?.shift();

    if (existing) {
      if (existing !== id) {
        const path = location.pathname.replace(uuidRegex, id);
        navigate(path, { replace: true });
      }
    } else {
      navigate(location.pathname + '/' + id, { replace: true });
    }
  }

  return (
    <ListPageWrapper>
      <OutdatedLeadsModal agentId={contextAgentId} />
      <Stack gap={3} direction={'row'} alignItems={'flex-end'} mb={2}>
        <Tabs value={mainTab} sx={{flex: 1}} variant="scrollable">
          {isAlfa && <Tab to={getLink(`inbox`)} value={'inbox'} label={'Inbox'} />}
          <Tab to={getLink(`today-sql`)} value={'today-sql'} sx={{background: 'radial-gradient(circle,rgba(255, 242, 158, 1) 70%, rgba(255, 255, 255, 0) 100%)'}} label={<TabLabel label={counters?.todaySQL}>Todays SQLs</TabLabel>} />
          <Tab to={getLink(`today-follow-ups`)} value={'today-follow-ups'} sx={{background: 'radial-gradient(circle,rgba(255, 242, 158, 1) 80%, rgba(255, 255, 255, 0) 100%)'}} label={<TabLabel label={counters?.todayFollowUps}>Todays Follow-Ups</TabLabel>} />
          <Tab to={getLink(`same-day`)} value={'same-day'} sx={{background: 'radial-gradient(circle,rgba(255, 242, 158, 1) 70%, rgba(255, 255, 255, 0) 100%)'}} label={<TabLabel label={'n/a'}>Same Day Follow-Ups</TabLabel>} />
          <Tab to={getLink(`future-sql`)} value={'future-sql'} label={<TabLabel label={counters?.futureSQL}>Future SQLs</TabLabel>} />
          <Tab to={getLink(`future-follow-ups`)} value={'future-follow-ups'} label={<TabLabel label={counters?.futureFollowUps}>Future Follow-Ups</TabLabel>} />
          <Tab to={getLink(`missed-apps`)} value={'missed-apps'} label={<TabLabel label={counters?.missingApps}>Missed Appt.</TabLabel>} />
          <Tab to={getLink(`contacted`)} value={'contacted'} label={<TabLabel label={counters?.contacted}>Contacted</TabLabel>} />
          <Tab to={getLink(`goals`)} value={'goals'} label={`Goals & Metrics`} />
        </Tabs>

        {isManager && (<div>
          <AgentSelector value={agent} onChange={changeAgent} label={''} />
        </div>)}
      </Stack>

      <Outlet />
    </ListPageWrapper>
  );
};

const Tab = (props: TabProps & LinkProps) => (
  <MuiTab  component={Link} iconPosition={'start'} {...props} />
)

const useAgent = (defaultAgent = '') => {
  return useSessionStorageState<string>(
    'dashboardAgent',
    defaultAgent
  );
};
